import { formatClinicDate } from '../../utils/clinicTime';
import { describeTimings, parseTimings } from '../../utils/doctorTimings';

// The words and colours for a day's attendance status, shared by My Attendance, the board and the
// register. The status itself is worked out on the server (AttendanceDay.java); this only shows it.

export const STATUS = {
  PRESENT:    { label: 'Present',     short: 'P',  chip: 'bg-emerald-100 text-emerald-800', cell: 'bg-emerald-100 text-emerald-800' },
  HALF_DAY:   { label: 'Half day',    short: 'H',  chip: 'bg-amber-100 text-amber-800',     cell: 'bg-amber-100 text-amber-800' },
  ABSENT:     { label: 'Absent',      short: 'A',  chip: 'bg-red-100 text-red-800',         cell: 'bg-red-100 text-red-800' },
  WEEKLY_OFF: { label: 'Weekly off',  short: 'O',  chip: 'bg-gray-100 text-gray-600',       cell: 'bg-gray-100 text-gray-400' },
  HOLIDAY:    { label: 'Holiday',     short: 'Hd', chip: 'bg-teal-100 text-teal-800',       cell: 'bg-teal-100 text-teal-800' },
  ON_LEAVE:   { label: 'On leave',    short: 'L',  chip: 'bg-navy-100 text-navy-800',       cell: 'bg-navy-100 text-navy-800' },
  NOT_IN:     { label: 'Not in yet',  short: '',   chip: 'bg-gray-100 text-gray-600',       cell: 'bg-white text-gray-300' },
  NONE:       { label: '',            short: '',   chip: 'bg-gray-50 text-gray-400',        cell: 'bg-white text-gray-300' },
};

export const statusOf = (day) => STATUS[day?.status] || STATUS.NONE;

/** The legend under the register and the week strip, kept in one place. */
export const STATUS_LEGEND = 'P present · H half day · A absent · O weekly off · L on leave · Hd holiday';

/** "Present", "Present, late 12 min", "Half day, 3.5 h", "On leave (Casual)". */
export const describeDay = (day) => {
  if (!day) return '';
  let base = statusOf(day).label;
  if (day.status === 'HOLIDAY' && day.holidayName) base = `Holiday: ${day.holidayName}`;
  if (day.status === 'ON_LEAVE' && day.leave?.typeName) {
    base = `On leave (${day.leave.typeName}${day.leave.paid === false ? ', unpaid' : ''})`;
  }
  const parts = [];
  if (day.late && day.lateMinutes > 0) parts.push(`late ${day.lateMinutes} min`);
  if (day.hoursWorked > 0 && day.status !== 'PRESENT') parts.push(`${formatHours(day.hoursWorked)}`);
  // Half-day leave sits alongside whatever the punches made of the rest of the day.
  if (day.leave?.half && day.status !== 'ON_LEAVE') parts.push(`half day ${day.leave.typeName} leave`);
  return parts.length ? `${base}, ${parts.join(', ')}` : base;
};

/** 7.5 -> "7.5 h", 0 -> "0 h", null -> "". */
export const formatHours = (hours) => {
  if (hours == null || Number.isNaN(Number(hours))) return '';
  const n = Number(hours);
  return `${Number.isInteger(n) ? n : n.toFixed(1)} h`;
};

// Why the server flagged the day (AttendanceDay.flagCause). Away on a real position is the default,
// the one wording there was before the cause was sent.
const FLAG_LABEL = {
  OUTSIDE: 'Outside hospital, to review',
  APPROXIMATE: 'Approximate location, to review',
  NO_POSITION: 'No position, to review',
};

/** The flags a day or entry carries that someone should look at. */
export const dayFlags = (day) => {
  const flags = [];
  if (day?.flagged) flags.push({ key: 'flagged', label: FLAG_LABEL[day.flagCause] || FLAG_LABEL.OUTSIDE, tone: 'amber' });
  if (day?.rejected) flags.push({ key: 'rejected', label: 'Punch rejected', tone: 'red' });
  if (day?.autoClosed) flags.push({ key: 'autoClosed', label: 'No punch-out, closed by the system', tone: 'amber' });
  if (day?.corrected) flags.push({ key: 'corrected', label: 'Corrected', tone: 'gray' });
  return flags;
};

/** The chip for a leave request's state. */
export const LEAVE_STATUS = {
  PENDING:   { label: 'Pending',   chip: 'bg-amber-100 text-amber-800' },
  APPROVED:  { label: 'Approved',  chip: 'bg-emerald-100 text-emerald-800' },
  REJECTED:  { label: 'Rejected',  chip: 'bg-red-100 text-red-800' },
  CANCELLED: { label: 'Cancelled', chip: 'bg-gray-100 text-gray-600' },
};

/** "1 day", "0.5 days", "3 days". */
export const formatDays = (days) => {
  const n = Number(days);
  if (!Number.isFinite(n)) return '';
  const shown = Number.isInteger(n) ? n : n.toFixed(1);
  return `${shown} ${n === 1 ? 'day' : 'days'}`;
};

export const REVIEW_LABEL = {
  NONE: 'Not needed',
  FLAGGED: 'To review',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
};

/**
 * A fix this rough or rougher is an approximate location, not a position. Android sends one, blurred
 * to a 2 km grid with an accuracy of exactly 2000 m, when the browser has only approximate location;
 * an iPhone with Precise Location off does much the same. It lands a kilometre or more from wherever
 * the phone really is, so it places nobody. The same figure is GeoDistance.APPROXIMATE_FROM_M on the
 * server and APPROXIMATE_FROM_M in public/punch.html: change all three together.
 */
export const APPROXIMATE_FROM_M = 1000;

export const isApproximateFix = (accuracy) => accuracy != null && Number(accuracy) >= APPROXIMATE_FROM_M;

/** Where this phone turns precise location back on for the browser it is using, as a sentence. */
export const preciseLocationSteps = (userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent) => {
  const ua = userAgent || '';
  if (/iPhone|iPad|iPod/.test(ua)) {
    const app = /CriOS/.test(ua) ? 'Chrome' : /EdgiOS/.test(ua) ? 'Edge' : /FxiOS/.test(ua) ? 'Firefox' : 'Safari Websites';
    return `On the iPhone, open Settings > Privacy & Security > Location Services > ${app} and turn on Precise Location.`;
  }
  if (/Android/.test(ua)) {
    const app = /SamsungBrowser/.test(ua) ? 'Samsung Internet' : /EdgA/.test(ua) ? 'Edge' : /Firefox/.test(ua) ? 'Firefox' : 'Chrome';
    return `On the phone, open Settings > Apps > ${app} > Permissions > Location and turn on Use precise location.`;
  }
  return "Turn on precise location for this browser in the phone's settings.";
};

/**
 * Where a punch was made, for somebody reviewing it. Reading this is the whole point of recording a
 * position, so it says what is actually known rather than only whether a check passed: an
 * unreviewable "not checked" told the owner nothing (2026-10-03).
 *
 *   verdict  the one-line answer
 *   tone     'ok' | 'warn' | 'plain'
 *   coords   "31.52045, 74.35875" when a position was recorded, else null
 *   mapUrl   somewhere to open those coordinates, else null
 *   accuracy a warning when the fix was too coarse to place anybody, else null
 *   hint     what to do about it, when there is something to do
 *
 * A punch the server judged away on an approximate location (side.approximate) reads as that, not
 * as a distance: the distance is wherever the phone's blurring put it (prod, 2026-10-21).
 */
export const describePosition = (side) => {
  if (!side || side.latitude == null || side.longitude == null) {
    return {
      verdict: 'No position recorded',
      tone: 'warn',
      coords: null,
      mapUrl: null,
      accuracy: null,
      hint: 'The phone did not give one, usually because location was refused or indoors with no signal.',
    };
  }
  const lat = Number(side.latitude);
  const lng = Number(side.longitude);
  const coords = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  const mapUrl = `https://www.google.com/maps?q=${lat},${lng}`;
  // A fix of hundreds of metres comes from the network, not from GPS, and cannot place anybody.
  const coarse = side.accuracyMeters != null && Number(side.accuracyMeters) > 200;
  const accuracy = side.accuracyMeters == null
    ? null
    : `Accurate to about ${formatDistance(side.accuracyMeters)}${coarse ? ', too coarse to place them exactly' : ''}`;

  if (side.withinRange == null) {
    return {
      verdict: 'Recorded, but not checked',
      tone: 'plain',
      coords,
      mapUrl,
      accuracy,
      hint: "The hospital's own position has not been set, so there is nothing to measure against. Set it under Settings, Timezone Settings.",
    };
  }
  if (side.approximate) {
    return {
      verdict: 'Approximate location only',
      tone: 'warn',
      coords,
      mapUrl,
      accuracy,
      hint: `The phone shared only an approximate location, blurred to about ${formatDistance(side.accuracyMeters)}, so this punch cannot show whether they were at the hospital or away. It usually means precise location is off for their browser; the punch screen tells them how to turn it on.`,
    };
  }
  return {
    verdict: side.withinRange
      ? `At the hospital (${formatDistance(side.distanceMeters)})`
      : `${formatDistance(side.distanceMeters)} from the hospital`,
    tone: side.withinRange ? 'ok' : 'warn',
    coords,
    mapUrl,
    accuracy,
    hint: null,
  };
};

/** "35 m" or "2.3 km". */
export const formatDistance = (metres) => {
  if (metres == null) return '';
  const m = Number(metres);
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
};

/**
 * Only the parts asked for: formatClinicDate always adds the day, month and year, so a weekday on
 * its own, or a month and year, needs the rest switched off.
 */
export const datePart = (date, opts) =>
  formatClinicDate(date, { day: undefined, month: undefined, year: undefined, ...opts }, '');

const DAY_LABEL = { MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun' };
export const WEEK_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
export const dayLabel = (code) => DAY_LABEL[code] || code;

/**
 * The day hours a schedule carries, whichever way the row phrases them. Each day keeps its own
 * times, in the same stored shape as a doctor's clinic timings, so there is one weekly-hours format
 * in the system rather than two.
 */
export const dayHoursOf = (schedule) => {
  if (!schedule) return {};
  if (Array.isArray(schedule.dayHours)) return parseTimings(JSON.stringify(schedule.dayHours));
  return parseTimings(schedule.dayHours);
};

/** "Mon – Sat: 9:00 AM – 5:00 PM", or each run of days that share their hours. */
export const describeSchedule = (schedule) => describeTimings(dayHoursOf(schedule));

/**
 * A role as staff say it rather than as the database stores it. Shared because the board, the hours
 * editor and the reports all name the same roles, and three copies of this map would drift apart.
 */
export const ROLE_LABEL = {
  ADMIN: 'Administrator', ACCOUNTS: 'Accounts', DOCTOR: 'Doctor', NURSE: 'Nurse', PHARMACIST: 'Pharmacist',
  DISPENSER: 'Dispenser', RECEPTIONIST: 'Receptionist', TECHNICIAN: 'Technician',
};

export const roleLabel = (role) => ROLE_LABEL[role] || role || '';
